import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { XMarkIcon, CameraIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { userProfileAPI } from '../services/api';

function UserSelfProfileModal({ isOpen, onClose, user, onSaved }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [profilePhotoUploading, setProfilePhotoUploading] = useState(false);
  const [profilePhotoUploadError, setProfilePhotoUploadError] = useState('');

  const emptyAddress = useMemo(
    () => ({ type: 'current', houseNo: '', street: '', landmark: '', city: '', state: '', country: '', pincode: '' }),
    []
  );
  const [formData, setFormData] = useState({
    officialEmail: '',
    profilePhotoUrl: '',
    personalEmail: '',
    personalMobileNumber: '',
    officeMobileNumber: '',
    linkedinUrl: '',
    gender: '',
    dateOfBirth: '',
    nationality: '',
    fatherName: '',
    bloodGroup: '',
    languagesSpoken: '',
    addresses: {
      current: { ...emptyAddress, type: 'current' },
      permanent: { ...emptyAddress, type: 'permanent' },
      sameAsCurrent: false
    },
    emergencyContacts: [{ name: '', phone: '', relation: '' }]
  });

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'personal', label: 'Personal' },
    { id: 'contact', label: 'Contact' },
    { id: 'address', label: 'Address' },
    { id: 'emergency', label: 'Emergency' }
  ];

  const mapUserToForm = useCallback((userData) => {
    const currentAddress = userData?.addresses?.find(a => a.type === 'current') || { ...emptyAddress, type: 'current' };
    const permanentAddress = userData?.addresses?.find(a => a.type === 'permanent') || { ...emptyAddress, type: 'permanent' };
    return {
      officialEmail: userData?.officialEmail || userData?.email || '',
      profilePhotoUrl: userData?.profilePhotoUrl || '',
      personalEmail: userData?.personalEmail || '',
      personalMobileNumber: userData?.personalMobileNumber || '',
      officeMobileNumber: userData?.officeMobileNumber || '',
      linkedinUrl: userData?.linkedinUrl || '',
      gender: userData?.gender || '',
      dateOfBirth: userData?.dateOfBirth || '',
      nationality: userData?.nationality || '',
      fatherName: userData?.fatherName || '',
      bloodGroup: userData?.bloodGroup || '',
      languagesSpoken: Array.isArray(userData?.languagesSpoken)
        ? userData.languagesSpoken.join(', ')
        : (userData?.languagesSpoken || ''),
      addresses: {
        current: { ...currentAddress },
        permanent: { ...permanentAddress },
        sameAsCurrent: false
      },
      emergencyContacts: userData?.emergencyContacts?.length
        ? userData.emergencyContacts
        : [{ name: '', phone: '', relation: '' }]
    };
  }, [emptyAddress]);

  useEffect(() => {
    if (!isOpen) return;
    const mergeProfileForForm = (profileUser) => ({
      ...(user || {}),
      ...(profileUser || {}),
      profilePhotoUrl: profileUser?.profilePhotoUrl || user?.profilePhotoUrl || ''
    });

    setActiveTab('overview');
    userProfileAPI.getProfile()
      .then((response) => setFormData(mapUserToForm(mergeProfileForForm(response.data.user))))
      .catch(() => setFormData(mapUserToForm(user)));
    setErrors({});
    setProfilePhotoUploadError('');
  }, [isOpen, user, mapUserToForm]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddressChange = (type, field, value) => {
    setFormData(prev => ({
      ...prev,
      addresses: {
        ...prev.addresses,
        [type]: {
          ...prev.addresses[type],
          [field]: value
        }
      }
    }));
  };

  const handleSameAsCurrent = (checked) => {
    setFormData(prev => ({
      ...prev,
      addresses: {
        ...prev.addresses,
        sameAsCurrent: checked,
        permanent: checked ? { ...prev.addresses.current, type: 'permanent' } : prev.addresses.permanent
      }
    }));
  };

  const updateEmergency = (index, field, value) => {
    setFormData(prev => {
      const list = [...prev.emergencyContacts];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, emergencyContacts: list };
    });
  };

  const addEmergency = () => {
    setFormData(prev => ({
      ...prev,
      emergencyContacts: [...prev.emergencyContacts, { name: '', phone: '', relation: '' }]
    }));
  };

  const removeEmergency = (index) => {
    setFormData(prev => {
      const list = [...prev.emergencyContacts];
      list.splice(index, 1);
      return { ...prev, emergencyContacts: list.length ? list : list };
    });
  };

  const handleProfilePhotoUpload = (file) => {
    if (!file) return;
    setProfilePhotoUploadError('');
    setProfilePhotoUploading(true);
    const token = localStorage.getItem('token');

    const form = new FormData();
    form.append('file', file);
    form.append('folder', 'profile-photos');

    axios.post('/api/uploads/direct', form, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
    })
      .then((response) => {
        if (response.data?.publicUrl) {
          setFormData(prev => ({ ...prev, profilePhotoUrl: response.data.publicUrl }));
        }
      })
      .catch(async () => {
        setProfilePhotoUploadError('Upload failed. Please try again.');
      })
      .finally(() => {
        setProfilePhotoUploading(false);
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    const addresses = [];
    const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';
    const hasAddressData = (address) => Object.keys(address || {}).some(key => key !== 'type' && hasValue(address[key]));

    if (hasAddressData(formData.addresses.current)) {
      addresses.push({ ...formData.addresses.current, type: 'current' });
    }
    const permanentAddress = formData.addresses.sameAsCurrent
      ? { ...formData.addresses.current, type: 'permanent' }
      : { ...formData.addresses.permanent, type: 'permanent' };
    if (hasAddressData(permanentAddress)) {
      addresses.push(permanentAddress);
    }

    try {
      const payload = {
        profilePhotoUrl: formData.profilePhotoUrl || null,
        personalEmail: formData.personalEmail || null,
        personalMobileNumber: formData.personalMobileNumber || null,
        officeMobileNumber: formData.officeMobileNumber || null,
        linkedinUrl: formData.linkedinUrl || null,
        gender: formData.gender || null,
        dateOfBirth: formData.dateOfBirth || null,
        nationality: formData.nationality || null,
        fatherName: formData.fatherName || null,
        bloodGroup: formData.bloodGroup || null,
        languagesSpoken: formData.languagesSpoken
          ? formData.languagesSpoken.split(',').map(l => l.trim()).filter(Boolean)
          : null,
        addresses,
        emergencyContacts: (formData.emergencyContacts || []).filter(row =>
          hasValue(row.name) || hasValue(row.phone) || hasValue(row.relation)
        )
      };

      const response = await userProfileAPI.updateProfile(payload);
      if (response.data?.user && typeof onSaved === 'function') {
        onSaved(response.data.user);
      }
      onClose();
    } catch (error) {
      setErrors({ submit: error.response?.data?.error || 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" onClick={onClose} />
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full border border-slate-200">
          <div className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-950 px-6 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-white">Edit Profile</h3>
                <p className="text-white/70 text-sm mt-1">Keep your profile fresh and accurate.</p>
              </div>
              <button onClick={onClose} className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="bg-white px-6 pt-6 pb-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">
                  {errors.submit}
                </div>
              )}

              <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      activeTab === tab.id ? 'bg-slate-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeTab === 'overview' && (
                <>
                  <div className="flex items-start sm:items-center gap-4 sm:gap-6">
                    <div
                      data-testid="self-profile-photo-preview"
                      className="h-20 w-20 shrink-0 flex-shrink-0 rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center"
                    >
                      {formData.profilePhotoUrl ? (
                        <img src={formData.profilePhotoUrl} alt="Profile" className="h-full w-full object-cover" />
                      ) : (
                        <CameraIcon className="h-8 w-8 text-slate-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Profile Photo</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleProfilePhotoUpload(e.target.files?.[0])}
                        disabled={profilePhotoUploading}
                        className="max-w-full text-sm"
                      />
                      {profilePhotoUploading && <div className="text-xs text-indigo-600 mt-1">Uploading...</div>}
                      {profilePhotoUploadError && <div className="text-xs text-red-600 mt-1">{profilePhotoUploadError}</div>}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Full Name</label>
                      <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-700">
                        {`${user?.firstName || ''} ${user?.lastName || ''}`.trim()}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Official Email</label>
                      <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-700">
                        {user?.officialEmail || formData?.officialEmail || user?.email || '—'}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {activeTab === 'personal' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Gender</label>
                      <select
                        value={formData.gender}
                        onChange={(e) => handleChange('gender', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5"
                      >
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Date of Birth</label>
                      <input
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Nationality</label>
                      <input
                        type="text"
                        value={formData.nationality}
                        onChange={(e) => handleChange('nationality', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Blood Group</label>
                      <input
                        type="text"
                        value={formData.bloodGroup}
                        onChange={(e) => handleChange('bloodGroup', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">Languages Spoken</label>
                    <input
                      type="text"
                      value={formData.languagesSpoken}
                      onChange={(e) => handleChange('languagesSpoken', e.target.value)}
                      className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5"
                      placeholder="English, Hindi"
                    />
                  </div>
                </>
              )}

              {activeTab === 'contact' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Personal Email</label>
                      <input
                        type="email"
                        value={formData.personalEmail}
                        onChange={(e) => handleChange('personalEmail', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Personal Mobile</label>
                      <input
                        type="tel"
                        value={formData.personalMobileNumber}
                        onChange={(e) => handleChange('personalMobileNumber', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Office Mobile</label>
                      <input
                        type="tel"
                        value={formData.officeMobileNumber}
                        onChange={(e) => handleChange('officeMobileNumber', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">LinkedIn</label>
                      <input
                        type="text"
                        value={formData.linkedinUrl}
                        onChange={(e) => handleChange('linkedinUrl', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5"
                      />
                    </div>
                  </div>
                </>
              )}

              {activeTab === 'address' && (
                <>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <h4 className="text-base font-bold text-gray-900 mb-3">Current Address</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        value={formData.addresses.current.houseNo}
                        onChange={(e) => handleAddressChange('current', 'houseNo', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2"
                        placeholder="Flat / House No"
                      />
                      <input
                        type="text"
                        value={formData.addresses.current.street}
                        onChange={(e) => handleAddressChange('current', 'street', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2"
                        placeholder="Street / Area"
                      />
                      <input
                        type="text"
                        value={formData.addresses.current.landmark}
                        onChange={(e) => handleAddressChange('current', 'landmark', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2"
                        placeholder="Landmark"
                      />
                      <input
                        type="text"
                        value={formData.addresses.current.city}
                        onChange={(e) => handleAddressChange('current', 'city', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2"
                        placeholder="City"
                      />
                      <input
                        type="text"
                        value={formData.addresses.current.state}
                        onChange={(e) => handleAddressChange('current', 'state', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2"
                        placeholder="State"
                      />
                      <input
                        type="text"
                        value={formData.addresses.current.country}
                        onChange={(e) => handleAddressChange('current', 'country', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2"
                        placeholder="Country"
                      />
                      <input
                        type="text"
                        value={formData.addresses.current.pincode}
                        onChange={(e) => handleAddressChange('current', 'pincode', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2"
                        placeholder="Pincode"
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      id="sameAsCurrentUser"
                      type="checkbox"
                      checked={formData.addresses.sameAsCurrent}
                      onChange={(e) => handleSameAsCurrent(e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="sameAsCurrentUser" className="ml-2 block text-sm text-gray-900">
                      Permanent address same as current
                    </label>
                  </div>

                  {!formData.addresses.sameAsCurrent && (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                      <h4 className="text-base font-bold text-gray-900 mb-3">Permanent Address</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="text"
                          value={formData.addresses.permanent.houseNo}
                          onChange={(e) => handleAddressChange('permanent', 'houseNo', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2"
                          placeholder="Flat / House No"
                        />
                        <input
                          type="text"
                          value={formData.addresses.permanent.street}
                          onChange={(e) => handleAddressChange('permanent', 'street', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2"
                          placeholder="Street / Area"
                        />
                        <input
                          type="text"
                          value={formData.addresses.permanent.landmark}
                          onChange={(e) => handleAddressChange('permanent', 'landmark', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2"
                          placeholder="Landmark"
                        />
                        <input
                          type="text"
                          value={formData.addresses.permanent.city}
                          onChange={(e) => handleAddressChange('permanent', 'city', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2"
                          placeholder="City"
                        />
                        <input
                          type="text"
                          value={formData.addresses.permanent.state}
                          onChange={(e) => handleAddressChange('permanent', 'state', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2"
                          placeholder="State"
                        />
                        <input
                          type="text"
                          value={formData.addresses.permanent.country}
                          onChange={(e) => handleAddressChange('permanent', 'country', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2"
                          placeholder="Country"
                        />
                        <input
                          type="text"
                          value={formData.addresses.permanent.pincode}
                          onChange={(e) => handleAddressChange('permanent', 'pincode', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2"
                          placeholder="Pincode"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'emergency' && (
                <>
                  {formData.emergencyContacts.map((row, index) => (
                    <div key={`em-${index}`} className="border border-gray-200 rounded-xl p-4 space-y-3">
                      <div className="grid grid-cols-3 gap-4">
                        <input
                          type="text"
                          value={row.name}
                          onChange={(e) => updateEmergency(index, 'name', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2"
                          placeholder="Contact Name"
                        />
                        <input
                          type="tel"
                          value={row.phone}
                          onChange={(e) => updateEmergency(index, 'phone', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2"
                          placeholder="Mobile"
                        />
                        <input
                          type="text"
                          value={row.relation}
                          onChange={(e) => updateEmergency(index, 'relation', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2"
                          placeholder="Relation"
                        />
                      </div>
                      {formData.emergencyContacts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeEmergency(index)}
                          className="text-sm text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addEmergency}
                    className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 font-semibold text-sm hover:bg-slate-200"
                  >
                    + Add Emergency Contact
                  </button>
                </>
              )}
            </form>
          </div>

          <div className="bg-slate-50 px-6 py-4 sm:flex sm:flex-row-reverse gap-3 border-t border-slate-200">
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={saving}
              className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-3 rounded-xl text-base font-bold text-white bg-slate-900 hover:bg-slate-800 focus:outline-none focus:ring-4 focus:ring-slate-300 transition-all duration-200 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center items-center px-6 py-3 border border-slate-300 rounded-xl text-base font-semibold text-slate-700 bg-white hover:bg-slate-100 transition-all duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserSelfProfileModal;
